import type { DateValidationError, PickerChangeHandlerContext } from '@mui/x-date-pickers';
import { DatePicker as MuiComponent, DatePickerProps as MuiProps } from '@mui/x-date-pickers/DatePicker';
import { type ConfigType, Dayjs } from 'dayjs';
import React, { FC, useCallback, useState } from 'react';
import { breakpoints } from '../../setup/theme/impl/breakpoints';
import { BpDesignSystem } from '../../types';
import { dayjs, isoDateToLocalDate } from '../../utils';
import { useDateTimeFormat } from '../../utils/dateFormatters/useDateTimeFormat';
import { Input, InputProps } from '../Form/Input';
import { getHelperText } from '../Form/HelperText';

const isTestEnv = process.env.NODE_ENV === 'test';

export type DatePickerContext = PickerChangeHandlerContext<DateValidationError>;

export type DatePickerProps = BpDesignSystem.Props<
  Omit<MuiProps<Dayjs>, 'value' | 'minDate' | 'maxDate'> & {
    required?: boolean;
    error?: boolean;
    errorText?: string;
    subLabel?: string;
    helperText?: string;
    id?: string;
    fullWidth?: boolean;
    clearable?: boolean;
    placeholder?: string;
    minDateMessage?: string;
    maxDateMessage?: string;
    invalidDateMessage?: string;
    dateTime?: boolean;
    value?: ConfigType;
    minDate?: ConfigType;
    maxDate?: ConfigType;
  }
>;

export const DatePicker: FC<DatePickerProps> = ({
  dataTest,
  className,
  error,
  errorText,
  helperText,
  sx,
  style,
  fullWidth,
  clearable,
  slotProps,
  subLabel,
  slots,
  id,
  placeholder,
  format,
  minDateMessage,
  maxDateMessage,
  invalidDateMessage,
  onError,
  required,
  value,
  dateTime,
  minDate,
  maxDate,
  desktopModeMediaQuery = !isTestEnv ? `@media (min-width: ${breakpoints.md}px)` : undefined,
  ...rest
}) => {
  const { dateTimeFormat } = useDateTimeFormat();
  const [validationMessage, setValidationMessage] = useState<string | undefined>(undefined);
  const dayjsFormat = format ?? dateTimeFormat.short.replace(/d/g, 'D').replace(/y/g, 'Y');
  const targetMinDate = minDate ? dayjs(minDate) : undefined;
  const targetMaxDate = maxDate ? dayjs(maxDate) : undefined;
  const dayjsValue = value ? dayjs(value) : null;
  const targetValue =
    dateTime || isTimezonedDateObject(dayjsValue)
      ? dayjsValue
      : dayjsValue
        ? dayjs(isoDateToLocalDate(dayjsValue?.toDate()))
        : null;

  const handleError = useCallback(
    (reason: DateValidationError, value: Dayjs | null) => {
      const message = (() => {
        switch (reason) {
          case 'minDate':
          case 'disablePast':
            return minDateMessage;
          case 'maxDate':
          case 'disableFuture':
            return maxDateMessage;
          case 'invalidDate':
            return invalidDateMessage;
          default:
            return undefined;
        }
      })();
      setValidationMessage(reason ? message : undefined);
      onError?.(reason, value);
    },
    [minDateMessage, maxDateMessage, invalidDateMessage, onError]
  );

  const combinedError = error || !!validationMessage;
  const combinedErrorText = errorText ?? validationMessage;

  return (
    <MuiComponent
      value={targetValue}
      format={dayjsFormat}
      minDate={targetMinDate}
      maxDate={targetMaxDate}
      desktopModeMediaQuery={desktopModeMediaQuery}
      onError={handleError}
      slots={{
        ...slots,
        textField: Input
      }}
      slotProps={{
        ...slotProps,
        field: {
          clearable: !!clearable,
          onClear: () =>
            rest.onChange?.(null, {
              validationError: null
            }),
          ...slotProps?.field
        },
        textField: {
          'data-test': dataTest ?? 'date-picker',
          error: combinedError,
          required,
          sx,
          style,
          fullWidth,
          subLabel,
          placeholder,
          id,
          helperText:
            !!helperText || !!combinedErrorText
              ? getHelperText(combinedError, helperText, combinedErrorText)
              : undefined,
          ...(slotProps?.textField ?? {})
        } as unknown as InputProps
      }}
      {...rest}
      className={`date-picker ${className || ''}`}
    />
  );
};

function isTimezonedDateObject(date: Dayjs | null | undefined): boolean {
  if (!date || !dayjs.isDayjs(date)) {
    return false;
  }
  return date.toDate().getDate() !== isoDateToLocalDate(date.toDate())?.getDate();
}
